import * as React from 'react';
import { useCallback } from 'react';

import { AiJobStatusResponse } from '~api/ai/types';
import { useAtomValue, useSetAtom } from 'jotai';

import { translate } from '~l10n/client';
import { Dialog } from '~shared/components/Dialog';
import { usePollHistory } from '~shared/polling';
import {
  CurrentQuizEditorDialog,
  currentQuizEditorDialogAtom,
} from '~teacher/components/QuizEditor/atoms/currentQuizEditorDialogAtom';
import { useQuizContext } from '~teacher/components/QuizEditor/contexts/QuizContextProvider';
import { HideIntercomLauncher } from '~teacher/hook/utils/HideIntercomLauncher';

import {
  extractAddedQuestionSetAtom,
  extractQuestionsPositionAtom,
  extractQuestionsRequestIdAtom,
  extractQuestionsStepAtom,
} from './atoms';
import { ExtractQuestions } from './ExtractQuestions';

interface Props {
  onClose: () => void;
}

export function ExtractQuestionsDialog({ onClose }: Props) {
  const requestId = useAtomValue(extractQuestionsRequestIdAtom);
  const currentQuizEditorDialog = useAtomValue(currentQuizEditorDialogAtom);
  const setStep = useSetAtom(extractQuestionsStepAtom);
  const setCurrentDialog = useSetAtom(currentQuizEditorDialogAtom);
  const resetAddedQuestions = useSetAtom(extractAddedQuestionSetAtom.resetAtom);
  const { cleanup: clearPolling } = usePollHistory<AiJobStatusResponse['data']>(
    {
      requestId,
      cleanupOnUnmount: true,
    }
  );

  const isOpen =
    currentQuizEditorDialog === CurrentQuizEditorDialog.ExtractQuestionsFromDoc;

  const { quizId, workspaceQuizId, workspaceId } = useQuizContext();
  const position = useAtomValue(extractQuestionsPositionAtom);

  const handleDialogClose = useCallback(() => {
    clearPolling();
    resetAddedQuestions();
    setStep('form');
    setCurrentDialog(CurrentQuizEditorDialog.None);
    onClose();
  }, [clearPolling, resetAddedQuestions, setStep, setCurrentDialog, onClose]);

  return (
    <Dialog
      position="right"
      open={isOpen}
      onClose={handleDialogClose}
      id="extract-questions-from-doc-dialog"
      size="medium"
      title={''}
      closeLabel={translate('Close')}
      disableBackdropClose
    >
      {isOpen && (
        <>
          <HideIntercomLauncher />
          <ExtractQuestions
            quizId={quizId}
            workspaceQuizId={workspaceQuizId}
            workspaceId={workspaceId}
            insertionOrder={position}
            onClose={handleDialogClose}
          />
        </>
      )}
    </Dialog>
  );
}
